import { useQuery, type UseQueryResult } from "@tanstack/react-query"

import { ApiError as HttpApiError } from "@/lib/errors"
import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"

import { isRunning, type Review, type ReviewId } from "../data/review-data"

import { fromWireReview } from "./adapter"
import type { WireReview } from "./wire"

/**
 * Reads a review by id via React Query.
 *
 * Invariants preserved from the earlier custom implementation:
 *   • AbortSignal-aware: React Query cancels the fetch when the id changes
 *     or the component unmounts, preventing a stale response from clobbering
 *     a fresh one during rapid navigation.
 *   • Stale-while-revalidate: the cached view renders instantly while a
 *     background refetch checks freshness. Configure `staleTime` on the
 *     shared QueryClient (see `app/query-client.ts`) to tune the SWR window.
 *   • 404 → `null` data with no error: the caller distinguishes "loading",
 *     "not found", and "loaded" without also branching on error.status.
 *   • WebSocket-driven invalidation: consumers can call
 *     `queryClient.invalidateQueries({ queryKey: qk.reviews.detail(id) })`
 *     when a live frame lands, and every subscriber re-renders.
 */

async function fetchReview(id: ReviewId, signal: AbortSignal): Promise<Review | null> {
  try {
    const wire = await http.get<WireReview>(`/api/v1/reviews/${encodeURIComponent(id)}`, { signal })
    return fromWireReview(wire)
  } catch (err) {
    // 404: no such review. 422: the id slot is int-typed server-side, so a non-numeric id fails
    // path validation. To the reader the answer is the same: this review is not here.
    if (err instanceof HttpApiError && (err.status === 404 || err.status === 422)) return null
    throw err
  }
}

/** 1.5s is fast enough to feel live without being a load; the lane below is the exception. */
const POLL_MS = 1_500

/**
 * A review parked on the overnight lane is `running` for hours. Polling that every
 * second and a half would be tens of thousands of requests to watch nothing happen,
 * and a minute is already finer than the answer's own resolution.
 */
const BATCH_POLL_MS = 60_000

export interface UseReviewQueryOptions {
  /** Whether to keep polling while the review is running. Defaults to true. */
  poll?: boolean
}

export function useReviewQuery(
  id: ReviewId | null,
  options: UseReviewQueryOptions = {},
): UseQueryResult<Review | null> {
  const { poll = true } = options
  return useQuery({
    queryKey: id != null ? qk.reviews.detail(id) : ["reviews", "detail", "none"],
    queryFn: ({ signal }) => fetchReview(id as ReviewId, signal),
    enabled: id != null,
    // A review that is still running has to refresh itself: its findings arrive
    // stage by stage, and the page would otherwise sit on an empty list that
    // reads as a clean pass.
    refetchInterval: (query) => {
      if (!poll) return false
      const review = query.state.data
      if (!review || !isRunning(review.status)) return false
      return review.pipelineStage === "batch_wait" ? BATCH_POLL_MS : POLL_MS
    },
  })
}
